import { PrototypeApiError } from "./apiTransport";
import { openModal } from "./navigationController";
import { openPaywallWithLayout } from "./commerceController";
import { recordRuntimeError } from "./prototypeRuntimeSupport";
import type { PlanCode, PrototypeRuntime } from "./prototypeTypes";
import type { Root } from "./prototypeTypes";
import type { PrototypeState } from "./controllerState";
import type { GuardIntent } from './clientIntentPolicy';
import { showAuthModalIntentNote } from './formProfileController';

export { checkClientIntent } from './clientIntentPolicy';
export type { ClientIntent, GuardIntent, IntentDecision } from './clientIntentPolicy';

/**
 * Routes an API error to the right product-level guard rail.
 *
 *   - 401 → opens the login modal with a context-aware prompt.
 *   - 403 entitlement_required / ai_quota_exceeded → opens the paywall
 *     modal targeted at the plan that unlocks the action.
 *   - Anything else → falls back to the silent recordRuntimeError sink.
 *
 * Returns true when the error was intercepted by a guard, false when the
 * caller should keep handling it.
 */
export function handleApiError(
  runtime: PrototypeRuntime,
  plan: PlanCode,
  isLoggedIn: boolean,
  error: unknown,
  options: { root: Root; intent: GuardIntent },
): boolean {
  if (!(error instanceof PrototypeApiError)) {
    recordRuntimeError(runtime, error);
    return false;
  }
  const body = (error.body ?? {}) as Record<string, unknown>;
  if (error.status === 401) {
    recordRuntimeError(runtime, error);
    promptLogin(options.root, options.intent);
    return true;
  }
  if (error.status === 403) {
    const errorCode = typeof body.error === "string" ? body.error : "";
    // The server emits two different codes for plan-gate rejections:
    //   - "entitlement_required" — from the canonical entitlementGuard
    //     (idea submission, voting, commenting, favorites, progress).
    //   - "plan_upgrade_required" — from aiJobSubmission, which bypasses
    //     the canonical guard and does its own plan-entitlement check.
    // Both must trigger the paywall; otherwise a Free viewer hitting the
    // AI Create boundary sees a generic error toast instead of the
    // upgrade modal.
    if (errorCode === "entitlement_required" || errorCode === "plan_upgrade_required" || errorCode === "ai_quota_exceeded") {
      recordRuntimeError(runtime, error);
      promptUpgrade(options.root, options.intent, plan, errorCode);
      return true;
    }
  }
  recordRuntimeError(runtime, error);
  return false;
}

function promptLogin(root: Root, intent: GuardIntent) {
  openModal(root, "loginModal");
  setLoginIntent(root, intent);
}

function promptUpgrade(root: Root, intent: GuardIntent, plan: PlanCode, reason: string) {
  // Look up the live PrototypeState so the Free card can be hidden
  // for paid users (no in-app downgrade path).
  const liveState = lookupState(root);
  if (liveState) {
    openPaywallWithLayout(root, liveState, {
      setIntent: (r) => setPaywallIntent(r, intent, plan, reason),
    });
    return;
  }
	// Fallback: no runtime state should never occur in the application. Keep
	// the chooser fail-closed rather than reviving the retired price painter.
	// Runtime hydration will mount the same chooser once session state exists.
	void plan;
  setPaywallIntent(root, intent, plan, reason);
  openModal(root, "paywallModal");
}

function lookupState(root: Root): PrototypeState | null {
  // PrototypeController stashes the live state on the root under a
  // private symbol-like key so guard helpers can reach it without a
  // full import cycle. This mirrors how other controllers share the
  // state through the root element.
  const stash = (root as unknown as { __prototypeState?: PrototypeState }).__prototypeState;
  return stash ?? null;
}

function setLoginIntent(root: Root, intent: GuardIntent) {
  showAuthModalIntentNote(root, 'login', loginIntentCopy(intent));
}

function setPaywallIntent(root: Root, intent: GuardIntent, plan: PlanCode, reason: string) {
  const note = lookupById(root, "paywallIntentNote");
  if (note) {
    note.textContent = paywallIntentCopy(intent, plan, reason);
    note.style.display = "";
  }
  const target = lookupById(root, "paywallTargetPlan");
  if (target) target.textContent = requiredPlanCopy(intent);
}

function lookupById(root: Root, id: string): HTMLElement | null {
  if (typeof document === "undefined") return null;
  if ("getElementById" in (root as Document)) {
    return (root as Document).getElementById(id);
  }
  if (root instanceof HTMLElement) {
    return root.querySelector<HTMLElement>(`#${id}`);
  }
  return null;
}

function loginIntentCopy(intent: GuardIntent): string | null {
  switch (intent) {
    case "favorite":
      return "Sign in to save favorites and pick up where you left off across devices.";
    case "progress":
      return "Sign in to track your chapter progress and recently opened lessons.";
    case "vote":
      // P4.1: Free 用户已开放投票能力，登录即可投票（不再需要升级 Lite）
      return "Sign in to vote on community topics. All signed-in accounts can vote.";
    case "comment":
      // P4.1: Free 用户已开放评论能力，登录即可评论
      return "Sign in to join the conversation. All signed-in accounts can comment.";
    case "idea":
      // P4.1: Free 用户已开放提案能力，登录即可提交
      return "Sign in to propose a community topic and watch it become a structured course.";
    case "feedback":
      return "Sign in to attach this feedback to your account for follow-up.";
    case "ai":
      return "Sign in to generate a private AI course on any topic.";
    default:
      return null;
  }
}

function paywallIntentCopy(intent: GuardIntent, plan: PlanCode, reason: string): string {
  if (reason === "ai_quota_exceeded") {
    if (plan === "max") {
      return "You've used all your AI Create credits for this month. Your quota resets next cycle - try again then.";
    }
    return "You've used all your AI Create credits for this month. Invite friends to earn more credits, or upgrade to Max for the highest monthly quota.";
  }
  switch (intent) {
    case "ai":
      return "AI Create is available to all signed-in users with credits. Invite friends to earn credits, or upgrade to Pro/Max for more monthly quota.";
    // P4.1: Community 写能力（vote/comment/idea）对所有登录用户开放，
    // 不再有 "Lite and above" 差异化文案；Free 用户登录后即可使用。
    // 此处 vote 分支不再触发，仅保留作为防御性 fallback。
    case "vote":
      return "Sign in to vote on community topics. All signed-in accounts can vote.";
    default:
      return "This feature is part of a paid plan. Upgrade to keep going.";
  }
}

export function requiredPlanCopy(intent: GuardIntent): string {
  // P4.1: Community 三项写能力（idea/vote/comment）对所有登录用户开放，
  // 最低要求是 Free（即登录），不再是 Lite。
  // AI Create: quota checked server-side; any logged-in user with credits can create.
  if (intent === "ai") return "Free";
  if (intent === "idea" || intent === "vote" || intent === "comment") return "Free";
  return "Free";
}
